import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import MealType from 'models/MealType';
import MaterialIcon from 'react/elements/MaterialIcon';
import NamePicker from 'react/components/search/NamePicker';
import { findIdeaNamed, normalizeIdeaName } from './searchMealIdeas';
import mealName from './mealName';
import BoughtListItems from './BoughtListItems';
import DayEventsPanel from './DayEventsPanel';
import { eventTimeLabel } from './calendarEventTime';
import GroceryItemEditor from '../groceryItems/GroceryItemEditor';
import MealIdeaPanel from '../mealIdeas/MealIdeaPanel';
import { groceryListItemNames, groceryListLabel } from './groceryListLabel';
import { mealKey } from '../../../redux/food/normalize';
import { dayHasMealIdeaInSlot, mealsInSlot, slotNoteOf } from '../../../redux/food/dayEvents';
import { NEEDS_CONNECTION_TITLE, onlineOnly } from '../../hooks/useOffline';

const NO_EVENTS = [];
const NO_LISTS = [];

/** A bought list's rows that have a name, in order. */
const namedRows = (list) => (list.rows || []).filter((row) => row.name);

export const addModes = {
	MEAL: 'MEAL',
	ITEM: 'ITEM',
};

const addModeOptions = [
	{ mode: addModes.MEAL, label: 'Meal', icon: 'restaurant_menu' },
	{ mode: addModes.ITEM, label: 'Grocery item', icon: 'shopping_basket' },
];

/**
 * What a tap on a calendar day opens: the day's meals under the four slots and Other, each slot a
 * row that opens that slot. A slot shows the meals it holds, its note, and under those a meal idea
 * picker for adding more. Tapping one of its meals opens its meal idea for editing, where removing
 * the meal from the slot also lives; a meal with no idea to open asks straight away whether to remove
 * it, and offers to make it a meal idea (`onConvertMeal`). On a phone a day's chips are too small
 * to aim at one by one, so the day is the target and this is where the choosing happens.
 *
 * Under the slots is the day's Events row, `calendarEvents` (from the food store, as
 * selectCalendarEvents gives them), which opens DayEventsPanel in the popup's place for adding,
 * editing and deleting them through `onSaveEvent`. `date` is the day, a moment; `dayEvent` is its
 * meals and slot notes as selectDayEvents gives them, or none for a day with nothing on it yet.
 *
 * The day's bought lists are here as well, since the day no longer has buttons for them: `groceryLists`,
 * from the food store as selectBoughtListsByDay gives them.
 * A bought list shows how much was bought and what, and opens here, the way a slot does, to the
 * items with their meal ideas and a button putting each back on the active list; `boughtItemActions`
 * is what BoughtListItems needs for that. An item or a meal idea tapped there opens in the list's
 * place, back arrow and all, rather than in a second popup that a short screen cuts off. From there
 * an item's ideas, its kinds and an idea's ingredients open the same way, each back arrow going back
 * one step; one already among those steps is gone back to rather than opened again.
 *
 * The search adds either a meal (a meal idea, or a new one) or, switched over, a single grocery item,
 * which goes on the slot by its name the way dragging it there does. Before anything is typed, each
 * offers what was most often in the slot lately: `commonBySlot[type]`, which `onOpenSlot` asks the
 * owner to load as a slot opens, as `{ meal_idea_ids, names }`. `groceryItems` is the budget's
 * catalog of grocery items (the food store's, in DayMealsModal), null while it is still loading.
 *
 * `offline` disables what is online-only, titled "Needs a connection": creating a meal idea, making a
 * meal's name one, and the day's events. Adding, removing and a slot's note are ops, and work offline.
 */
export default function DayMealsPopup({
	dayEvent = null,
	date = null,
	mealIdeas,
	busy = false,
	error = null,
	onAddIdea,
	onCreateIdea,
	onAddItem = null,
	onRemoveMeal,
	onConvertMeal = null,
	notice = null,
	onSaveSlotNote,
	onOpenMeal,
	boughtItemActions = {},
	commonBySlot = {},
	onOpenSlot = null,
	groceryItems = null,
	calendarEvents = NO_EVENTS,
	groceryLists = NO_LISTS,
	onSaveEvent = null,
	offline = false,
}) {
	const [addingTo, setAddingTo] = useState(null);
	const [eventsOpen, setEventsOpen] = useState(false);
	const [addMode, setAddMode] = useState(addModes.MEAL);
	const openSlot = (slot) => {
		setAddingTo(slot);
		if(onOpenSlot) {
			onOpenSlot(slot.id);
		}
	};
	const [confirmingMeal, setConfirmingMeal] = useState(null);
	const [openListKey, setOpenListKey] = useState(null);
	// What was opened from the list, in order: { kind: 'item', id } (a row of the list),
	// { kind: 'idea', id }, { kind: 'ingredient', ideaId, id } or { kind: 'grocery', id, name } (an
	// item on its own, from another's kinds). The last is shown.
	const [trail, setTrail] = useState([]);
	const slots = [...MealType.slots(), MealType.other()];
	const mealsIn = (type) => mealsInSlot(dayEvent, type);
	const ideaFor = (meal) => (meal.meal_idea_id ? mealIdeas.find((idea) => Number(idea.id) === Number(meal.meal_idea_id)) || null : null);
	// Gone once its last item is put back, which lands back on the day.
	const openList = groceryLists.find((groceryList) => groceryList.key === openListKey) || null;

	const steps = openList ? resolveTrail(trail, openList, mealIdeas) : [];
	const open = (step) => {
		const at = steps.findIndex((each) => showing(each) === showing({ step }));
		setTrail(at === -1 ? [...steps.map((each) => each.step), step] : steps.slice(0, at + 1).map((each) => each.step));
	};
	const back = () => setTrail(steps.slice(0, -1).map((step) => step.step));

	if(steps.length > 0) {
		const shown = steps[steps.length - 1];
		const backTo = steps.length > 1 ? steps[steps.length - 2].label : groceryListLabel(openList);
		const openIdea = (id) => open({ kind: 'idea', id });
		const openItem = (id, name) => open({ kind: 'grocery', id, name });
		return (
			<div className='day-meals-popup day-meals-popup--item'>
				{shown.step.kind === 'item' &&
				<GroceryItemEditor key={`item-${shown.item.id}`} row={shown.item} backTo={backTo} onClose={back} onOpenIdea={openIdea} onOpenItem={openItem} />
				}
				{shown.step.kind === 'idea' &&
				<MealIdeaPanel
					key={`idea-${shown.idea.id}`}
					idea={shown.idea}
					backTo={backTo}
					onClose={back}
					onOpenIngredient={(id) => open({ kind: 'ingredient', ideaId: shown.idea.id, id })}
				/>
				}
				{shown.step.kind === 'ingredient' &&
				<GroceryItemEditor
					key={`ingredient-${shown.ingredient.id}`}
					idea={shown.idea}
					ingredientId={shown.ingredient.id}
					onClose={back}
					onOpenIdea={openIdea}
					onOpenItem={openItem}
				/>
				}
				{shown.step.kind === 'grocery' &&
				<GroceryItemEditor
					key={`grocery-${shown.step.id}`}
					item={{ id: shown.step.id, name: shown.step.name }}
					backTo={backTo}
					onClose={back}
					onOpenIdea={openIdea}
					onOpenItem={openItem}
				/>
				}
			</div>
		);
	}

	if(openList) {
		return (
			<div className='day-meals-popup'>
				<div className='day-meals-popup__picker-heading'>
					<button
						type='button'
						className='btn-flat day-meals-popup__back'
						onClick={() => {
							setTrail([]);
							setOpenListKey(null);
						}}
						aria-label='Back to the day'
					>
						<MaterialIcon>arrow_back</MaterialIcon>
					</button>
					<span>{groceryListLabel(openList)}</span>
				</div>
				<BoughtListItems
					items={namedRows(openList)}
					{...boughtItemActions}
					onOpenItem={(item) => open({ kind: 'item', id: item.id })}
					onOpenIdea={(idea) => open({ kind: 'idea', id: idea.id })}
				/>
			</div>
		);
	}

	if(eventsOpen) {
		return (
			<DayEventsPanel
				date={date || moment(dayEvent.date, 'YYYY-MM-DD')}
				events={calendarEvents}
				busy={busy}
				offline={offline}
				onSave={onSaveEvent}
				onBack={() => setEventsOpen(false)}
			/>
		);
	}

	if(addingTo) {
		// Back to the day once the meal is on it; a failure keeps the picker, and the error, up.
		const done = (promise) => Promise.resolve(promise).then((added) => {
			if(added !== false) {
				setAddingTo(null);
			}
		});
		return (
			<div className='day-meals-popup'>
				<div className='day-meals-popup__picker-heading'>
					<button
						type='button'
						className='btn-flat day-meals-popup__back'
						onClick={() => {
							setConfirmingMeal(null);
							setAddingTo(null);
						}}
						aria-label='Back to the day'
					>
						<MaterialIcon>arrow_back</MaterialIcon>
					</button>
					<span>{addingTo.name}</span>
				</div>
				{error && <p className='day-meals-popup__error red-text'>{error}</p>}
				{notice && !error && <p className='day-meals-popup__notice'>{notice}</p>}
				{/* What the slot already holds, above the search, so a wrong meal can go before a right one is added. */}
				{mealsIn(addingTo.id).length > 0 &&
				<ul className='day-meals-popup__current'>
					{mealsIn(addingTo.id).map((meal) => (
						<CurrentMeal
							key={mealKey(meal)}
							meal={meal}
							slot={addingTo}
							idea={ideaFor(meal)}
							busy={busy}
							confirming={confirmingMeal === mealKey(meal)}
							onOpen={(idea) => {
								setConfirmingMeal(null);
								onOpenMeal(addingTo, meal, idea);
							}}
							onConfirm={() => setConfirmingMeal(mealKey(meal))}
							onCancel={() => setConfirmingMeal(null)}
							onRemove={() => {
								setConfirmingMeal(null);
								return onRemoveMeal(addingTo.id, meal);
							}}
							convert={onConvertMeal && normalizeIdeaName(meal.name) ? {
								offline,
								idea: findIdeaNamed(mealIdeas, meal.name),
								isItem: (groceryItems || []).some((item) => normalizeIdeaName(item.name) === normalizeIdeaName(meal.name)),
								onConvert: (options) => {
									setConfirmingMeal(null);
									return onConvertMeal(addingTo, meal, options);
								},
							} : null}
						/>
					))}
				</ul>
				}
				{onSaveSlotNote &&
				<SlotNote
					key={addingTo.id}
					slot={addingTo}
					notes={slotNoteOf(dayEvent, addingTo.id)}
					onSave={(notes) => onSaveSlotNote(addingTo.id, notes)}
				/>
				}
				{onAddItem ?
				<div className='day-meals-popup__mode' role='group' aria-label='What to add'>
					{addModeOptions.map((option) => (
						<button
							key={option.mode}
							type='button'
							className={`btn-flat day-meals-popup__mode-option${addMode === option.mode ? ' is-active' : ''}`}
							aria-pressed={addMode === option.mode}
							onClick={() => setAddMode(option.mode)}
						>
							<MaterialIcon>{option.icon}</MaterialIcon>
							{option.label}
						</button>
					))}
				</div> :
				<div className='day-meals-popup__add-heading'>Add a meal</div>
				}
				{addMode === addModes.ITEM && onAddItem ?
				<ItemPicker
					key={`item-${addingTo.id}`}
					slot={addingTo}
					meals={mealsIn(addingTo.id)}
					groceryItems={groceryItems}
					common={commonBySlot[addingTo.id]}
					busy={busy}
					onAdd={(name) => done(onAddItem(addingTo.id, name))}
				/> :
				<NamePicker
					key={`meal-${addingTo.id}`}
					items={mealIdeas}
					placeholder='Search meal ideas'
					emptyText='No meal ideas yet. Type a name to create one.'
					busy={busy}
					suggestions={commonIdeas(mealIdeas, commonBySlot[addingTo.id])}
					suggestionsHeading={`Often for ${addingTo.name.toLowerCase()}`}
					isTaken={(idea) => dayHasMealIdeaInSlot(dayEvent, addingTo.id, idea.id)}
					onPick={(idea) => done(onAddIdea(addingTo.id, idea))}
					onCreate={(name) => done(onCreateIdea(addingTo.id, name))}
					createDisabled={offline ? NEEDS_CONNECTION_TITLE : null}
				/>
				}
			</div>
		);
	}

	return (
		<div className='day-meals-popup'>
			{error && <p className='day-meals-popup__error red-text'>{error}</p>}
			<ul className='day-meals-popup__slots'>
				{slots.map((slot) => {
					const meals = mealsIn(slot.id);
					const note = slotNoteOf(dayEvent, slot.id);
					return (
						<li key={slot.id}>
							<button
								type='button'
								className={`btn-flat day-meals-popup__slot day-meals-popup__slot--${slot.key}`}
								data-slot={slot.key}
								onClick={() => openSlot(slot)}
							>
								<span className='day-meals-popup__slot-name'>{slot.name}</span>
								<span className='day-meals-popup__slot-content'>
									{meals.length > 0 &&
									<span className='day-meals-popup__meals'>
										{meals.map((meal) => (
											<span
												key={mealKey(meal)}
												className={`meal-chip meal-chip--${slot.key}`}
											>
												{mealName(meal, ideaFor(meal))}
											</span>
										))}
									</span>
									}
									{!!note &&
									<span className='day-meals-popup__slot-note'>{note}</span>
									}
								</span>
								<MaterialIcon className='day-meals-popup__add'>add</MaterialIcon>
							</button>
						</li>
					);
				})}
				<li>
					<button
						type='button'
						className='btn-flat day-meals-popup__slot day-meals-popup__slot--events'
						data-slot='events'
						onClick={() => setEventsOpen(true)}
					>
						<span className='day-meals-popup__slot-name'>Events</span>
						<span className='day-meals-popup__slot-content'>
							{calendarEvents.map((calendarEvent) => (
								<span key={`event-${calendarEvent.id}`} className='day-meals-popup__event'>
									<span className='day-meals-popup__event-time'>{eventTimeLabel(calendarEvent)}</span>
									<span className='day-meals-popup__event-name'>{calendarEvent.name}</span>
								</span>
							))}
						</span>
						<MaterialIcon className='day-meals-popup__add'>add</MaterialIcon>
					</button>
				</li>
			</ul>
			{groceryLists.length > 0 &&
			<div className='day-meals-popup__extras'>
				{groceryLists.map((groceryList) => (
					<button
						type='button'
						key={`list-${groceryList.key}`}
						className='btn-flat day-meals-popup__extra day-meals-popup__extra--grocery-list'
						onClick={() => setOpenListKey(groceryList.key)}
					>
						<MaterialIcon>shopping_cart</MaterialIcon>
						<span className='day-meals-popup__extra-content'>
							<span>{groceryListLabel(groceryList)}</span>
							<span className='day-meals-popup__extra-detail'>{groceryListItemNames(groceryList)}</span>
						</span>
						<MaterialIcon className='day-meals-popup__add'>chevron_right</MaterialIcon>
					</button>
				))}
			</div>
			}
		</div>
	);
}

/**
 * What a step of the trail shows, so one already open is gone back to: a list row and an item on its
 * own are the same when they are the same grocery item. `resolved` is a step from resolveTrail, or
 * `{ step }` for one about to be opened.
 */
function showing(resolved) {
	const { step } = resolved;
	if(step.kind === 'item') {
		return resolved.item ? `item-${Number(resolved.item.grocery_item_id)}` : `row-${step.id}`;
	}
	if(step.kind === 'grocery') {
		return `item-${Number(step.id)}`;
	}
	if(step.kind === 'ingredient') {
		return `ingredient-${Number(step.ideaId)}-${Number(step.id)}`;
	}
	return `idea-${Number(step.id)}`;
}

/**
 * The trail's steps with what each shows and the name its back arrow gives it, up to the first that
 * is gone: an item put back, or an idea or ingredient deleted. What was opened from it goes too.
 */
function resolveTrail(trail, list, mealIdeas) {
	const ideaById = (id) => mealIdeas.find((idea) => Number(idea.id) === Number(id)) || null;
	const steps = [];
	for(const step of trail) {
		if(step.kind === 'item') {
			const item = namedRows(list).find((each) => each.id === step.id);
			if(!item) {
				break;
			}
			steps.push({ step, item, label: item.name });
		} else if(step.kind === 'grocery') {
			steps.push({ step, label: step.name });
		} else if(step.kind === 'idea') {
			const idea = ideaById(step.id);
			if(!idea) {
				break;
			}
			steps.push({ step, idea, label: idea.name });
		} else {
			const idea = ideaById(step.ideaId);
			const ingredient = idea && (idea.ingredients || []).find((each) => Number(each.id) === Number(step.id));
			if(!ingredient) {
				break;
			}
			steps.push({ step, idea, ingredient, label: ingredient.name });
		}
	}
	return steps;
}

/** The slot's common ideas that still exist, in the order the server ranked them. */
function commonIdeas(mealIdeas, common) {
	if(!common) {
		return null;
	}
	const byId = new Map(mealIdeas.map((idea) => [Number(idea.id), idea]));
	return common.meal_idea_ids.map((id) => byId.get(Number(id))).filter(Boolean);
}

/** A budget has hundreds of items, and a loose search matches dozens; only the best few help. */
const ITEM_RESULTS = 8;

/**
 * The search over grocery items. Picking one, or typing a name that is none of them, puts that name
 * on the slot. One the slot already holds as an item of its own is marked rather than offered again.
 * Before anything is typed it offers the items most often in the slot lately, and with none of
 * those, nothing: a budget's every item is too long a list to scroll.
 */
function ItemPicker({ slot, meals, groceryItems, common, busy, onAdd }) {
	const items = groceryItems || [];
	const suggestions = common ? common.names.map((name) => {
		const wanted = normalizeIdeaName(name);
		return items.find((item) => normalizeIdeaName(item.name) === wanted) || { id: `name-${wanted}`, name };
	}) : null;
	const onSlot = new Set(meals.filter((meal) => !meal.meal_idea_id).map((meal) => normalizeIdeaName(meal.name)));
	return (
		<NamePicker
			items={items}
			placeholder='Search grocery items'
			emptyText={groceryItems === null ? 'Loading grocery items…' : 'Type an item to add it.'}
			busy={busy}
			suggestions={suggestions}
			suggestionsHeading={`Often for ${slot.name.toLowerCase()}`}
			listAllWhenBlank={false}
			maxResults={ITEM_RESULTS}
			isTaken={(item) => onSlot.has(normalizeIdeaName(item.name))}
			createLabel={(name) => <>Add &ldquo;{name}&rdquo;</>}
			onPick={(item) => onAdd(item.name)}
			onCreate={onAdd}
		/>
	);
}

/**
 * The slot's note, saved when the field loses focus. It follows the stored note until it is edited,
 * so a save that fails and puts the old note back shows that as well.
 */
function SlotNote({ slot, notes, onSave }) {
	const [draft, setDraft] = useState(null);
	const id = `day-meals-popup-note-${slot.key}`;
	// Leaving the slot, or closing the popup, can unmount the field without a blur.
	const pending = useRef({ draft, onSave });
	pending.current = { draft, onSave };
	useEffect(() => () => {
		if(pending.current.draft !== null) {
			pending.current.onSave(pending.current.draft);
		}
	}, []);
	const save = () => {
		if(draft !== null) {
			setDraft(null);
			onSave(draft);
		}
	};
	return (
		<div className='day-meals-popup__note'>
			<label htmlFor={id}>Notes</label>
			<textarea
				id={id}
				className='browser-default day-meals-popup__note-input'
				rows={2}
				placeholder={`Anything about ${slot.name.toLowerCase()} this day`}
				value={draft === null ? notes : draft}
				onChange={(event) => setDraft(event.target.value)}
				onBlur={save}
			/>
		</div>
	);
}

/**
 * A meal the slot already holds. One from a meal idea opens that idea for editing; one typed in by
 * hand, or whose idea is gone, has nothing to open, so a tap asks whether to remove it, and with
 * `convert`, offers to make its name a meal idea instead.
 */
function CurrentMeal({ meal, slot, idea, busy, confirming, onOpen, onConfirm, onCancel, onRemove, convert = null }) {
	const openable = !!idea && !!onOpen;
	return (
		<li className='day-meals-popup__current-meal'>
			<button
				type='button'
				className={`btn-flat day-meals-popup__current-meal-button${confirming ? ' is-open' : ''}`}
				onClick={() => {
					if(openable) {
						onOpen(idea);
					}
					else if(confirming) {
						onCancel();
					}
					else {
						onConfirm();
					}
				}}
			>
				<span className={`meal-chip meal-chip--${slot.key}`}>{mealName(meal, idea)}</span>
				<MaterialIcon>{openable ? 'edit' : 'delete'}</MaterialIcon>
			</button>
			{confirming &&
			<div className='day-meals-popup__meal-menu'>
				{convert && <ConvertToIdea meal={meal} busy={busy} {...convert} />}
				<RemoveConfirm meal={meal} slot={slot} busy={busy} onCancel={onCancel} onRemove={onRemove} />
			</div>
			}
		</li>
	);
}

/**
 * Makes the meal's name a meal idea, or links it to the idea already called that. Every meal of
 * the name on any day goes with it, and so do the bought list rows of the grocery item of that name.
 * When there is such an item, the idea can take it as an ingredient: right for a name that was
 * bought as one thing, wrong for one that stood for a whole recipe.
 */
function ConvertToIdea({ meal, busy, offline = false, idea, isItem, onConvert }) {
	const [withIngredient, setWithIngredient] = useState(false);
	const name = meal.name.trim();
	return (
		<div className='day-meals-popup__convert'>
			<button
				type='button'
				className='btn-flat day-meals-popup__convert-button'
				disabled={busy}
				{...onlineOnly(offline)}
				onClick={() => onConvert({ mealIdeaId: idea ? idea.id : null, withIngredient: isItem && withIngredient })}
			>
				<MaterialIcon>lightbulb</MaterialIcon>
				{idea ? <>Link to the meal idea &ldquo;{idea.name}&rdquo;</> : 'Make it a meal idea'}
			</button>
			<p className='day-meals-popup__convert-hint'>Every {name} on the calendar goes with it.</p>
			{isItem &&
			<label className='day-meals-popup__convert-ingredient'>
				<input
					type='checkbox'
					className='filled-in'
					checked={withIngredient}
					onChange={(event) => setWithIngredient(event.target.checked)}
				/>
				<span>Add the grocery item {name} as its ingredient</span>
			</label>
			}
		</div>
	);
}

/** Asks before a meal comes off its slot. */
function RemoveConfirm({ meal, slot, busy, onCancel, onRemove }) {
	return (
		<div className='day-meals-popup__confirm' role='alertdialog' aria-label={`Remove ${meal.name}`}>
			<p>Remove {meal.name} from {slot.name}?</p>
			<div className='day-meals-popup__confirm-buttons'>
				<button type='button' className='btn-flat day-meals-popup__confirm-cancel' onClick={onCancel}>
					Cancel
				</button>
				<button
					type='button'
					className='btn-flat day-meals-popup__confirm-remove'
					disabled={busy}
					onClick={onRemove}
				>
					Remove
				</button>
			</div>
		</div>
	);
}
